/* Xiaomi 2026 · Partner 360 view · Ayush + Rajan's workbench
 *
 * /xiaomi/partners/:retailerId
 *
 * Composition (Ayush-inspired, our additions):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Identity bar — photo · name · outlet · tier · zone · IDs    │
 *   │ 📌 Pinned standing instructions (cf_concierge_directive)     │
 *   │                                                              │
 *   │  ┌─ Active workflows ─┐  ┌─ Feedback ledger ─┐               │
 *   │  │ + Gift ops cards   │  │ Photos · videos   │               │
 *   │  └────────────────────┘  └───────────────────┘               │
 *   │  ┌─ 2026 plan ────────┐  ┌─ Notes & Audit ───┐               │
 *   │  │ Eligibility timeline│ │ + Zoho tickets    │               │
 *   │  └────────────────────┘  └───────────────────┘               │
 *   │  ┌─ Capture feedback ─────────────────────────┐              │
 *   │  │ Rating · text · drag-drop media · Save     │              │
 *   │  └────────────────────────────────────────────┘              │
 *   └──────────────────────────────────────────────────────────────┘
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import MediaUploadDropzone from '@/components/xiaomi/MediaUploadDropzone';
import MessageComposerModal from '@/components/xiaomi/MessageComposerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const TIER_CHIP = {
  'Platinum Luxe':      { bg: '#fef3c7', border: '#ca8a04', tone: '#713f12' },
  'Platinum Signature': { bg: '#fae8ff', border: '#a855f7', tone: '#581c87' },
  'Platinum Prime':     { bg: '#dbeafe', border: '#3b82f6', tone: '#1e3a8a' },
  'Rising Platinum':    { bg: '#dcfce7', border: '#16a34a', tone: '#14532d' },
};

const SENTIMENT_TONE = {
  positive: { icon: '😊', bg: '#f0fdf4', border: '#bbf7d0', tone: '#166534' },
  neutral:  { icon: '😐', bg: '#fafaf9', border: '#e7e5e4', tone: '#44403c' },
  negative: { icon: '😔', bg: '#fef2f2', border: '#fecaca', tone: '#b91c1c' },
};


function TierChip({ tier }) {
  const c = TIER_CHIP[tier] || { bg: '#f3f4f6', border: '#9ca3af', tone: '#374151' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, color: c.tone }}
      data-testid="tier-chip"
    >
      {tier?.replace('Platinum ', '') || 'No tier'}
    </span>
  );
}


function Initials({ name }) {
  const i = (name || '?').split(' ').filter(Boolean).slice(0, 2).map((x) => x[0]).join('');
  return (
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center text-[28px] font-bold text-white"
      style={{ background: 'linear-gradient(135deg,#ff6900,#cc5500)' }}
      data-testid="partner-avatar-initials"
    >
      {i.toUpperCase()}
    </div>
  );
}


function Panel({ title, count, testId, children, right }) {
  return (
    <section className="x-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] uppercase tracking-wider text-soft">
          {title} {count !== undefined && <span className="text-[#15110d]">· {count}</span>}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}


function ActiveInstancesPanel({ instances, dealerId, dealerLanguage, onRefresh }) {
  const [photoTarget, setPhotoTarget] = useState(null);
  const [composerInstance, setComposerInstance] = useState(null);
  if (!instances.length) {
    return <div className="text-sm text-soft">No active workflows yet · 1 June fires the first wave.</div>;
  }
  return (
    <ul className="space-y-2" data-testid="active-instances-list">
      {instances.map((i) => {
        const dispatch = i.dispatch_photo_url;
        const handover = i.handover_photo_url || i.dbr_handover_photo_url;
        return (
          <li key={i._id} className="border-l-2 border-[#ff6900] pl-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-[#15110d]">
                  {i.benefit_id?.replace('xiaomi_', '').replace(/_/g, ' ')}
                </div>
                <div className="text-[11px] text-soft">
                  Step {i.current_step} · {i.current_stage_key} ·{' '}
                  <span className="font-mono">{i.month_ym}</span>
                  {i.gift_ops_stage && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-[#fff7ed] text-[#7c2d12]">
                      gift: {i.gift_ops_stage}
                    </span>
                  )}
                </div>
                {i.zoho_ticket_id && (
                  <a
                    href={i.zoho_ticket_url || '#'} target="_blank" rel="noreferrer"
                    className="text-[11px] text-[#1e40af] hover:underline mt-1 inline-block"
                    data-testid={`zoho-ticket-link-${i.zoho_ticket_id}`}
                  >
                    Zoho ticket #{i.zoho_ticket_id} ↗
                  </a>
                )}
              </div>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#ecfdf5] text-[#065f46]">
                {i.state}
              </span>
            </div>
            {(dispatch || handover) && (
              <div className="flex gap-2 mt-1.5" data-testid={`instance-photos-${i._id}`}>
                {dispatch && (
                  <a href={dispatch} target="_blank" rel="noreferrer"
                    data-testid={`instance-dispatch-photo-${i._id}`}>
                    <img src={dispatch} alt="dispatch"
                      className="w-12 h-12 rounded object-cover border border-line" />
                    <div className="text-[9px] uppercase text-soft text-center mt-0.5">dispatch</div>
                  </a>
                )}
                {handover && (
                  <a href={handover} target="_blank" rel="noreferrer"
                    data-testid={`instance-handover-photo-${i._id}`}>
                    <img src={handover} alt="handover"
                      className="w-12 h-12 rounded object-cover border border-line" />
                    <div className="text-[9px] uppercase text-soft text-center mt-0.5">handover</div>
                  </a>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-1.5 flex-wrap">
              <button onClick={() => setComposerInstance(i)}
                className="text-[10px] px-2 py-0.5 rounded bg-[#15110d] text-white hover:bg-[#2a2419]"
                data-testid={`btn-compose-msg-${i._id}`}>
                💬 Compose WA message
              </button>
              {!dispatch && (
                <button onClick={() => setPhotoTarget({ id: i._id, kind: 'dispatch' })}
                  className="text-[10px] px-2 py-0.5 rounded border border-line text-soft hover:bg-[#fff7ed] hover:text-[#7c2d12]"
                  data-testid={`btn-attach-dispatch-${i._id}`}>
                  + Dispatch photo
                </button>
              )}
              {!handover && (
                <button onClick={() => setPhotoTarget({ id: i._id, kind: 'handover' })}
                  className="text-[10px] px-2 py-0.5 rounded border border-line text-soft hover:bg-[#fff7ed] hover:text-[#7c2d12]"
                  data-testid={`btn-attach-handover-${i._id}`}>
                  + Handover photo
                </button>
              )}
            </div>
          </li>
        );
      })}
      {photoTarget && (
        <InstancePhotoModal target={photoTarget}
          onClose={() => setPhotoTarget(null)}
          onSaved={() => { setPhotoTarget(null); onRefresh?.(); }} />
      )}
      {composerInstance && (
        <MessageComposerModal
          instance={composerInstance}
          dealerLanguage={dealerLanguage}
          benefitName={composerInstance.benefit_id?.replace('xiaomi_', '').replace(/_/g, ' ')}
          onClose={() => setComposerInstance(null)}
          onSaved={() => onRefresh?.()}
        />
      )}
    </ul>
  );
}


function InstancePhotoModal({ target, onClose, onSaved }) {
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!url || url.length < 8) {
      toast.error('Paste a valid URL');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/xiaomi/workflow/instance/${encodeURIComponent(target.id)}/photo`,
        { kind: target.kind, url, caption: caption || null },
        auth(),
      );
      toast.success(`${target.kind} photo attached`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={onClose} data-testid="instance-photo-modal">
      <div className="bg-white rounded-lg p-6 w-[420px] max-w-full"
        onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>
          Attach {target.kind} photo
        </h3>
        <p className="text-[11px] text-soft mt-1">
          {target.kind === 'dispatch'
            ? 'Photo of the packed gift before Kavita ships it out.'
            : 'Photo of the dealer (or unboxing) after final handover.'}
        </p>
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Paste Cloudinary / Drive URL"
          className="w-full mt-3 px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
          data-testid="instance-photo-url-input" />
        <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional · max 240 chars)"
          maxLength={240}
          className="w-full mt-2 px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
          data-testid="instance-photo-caption-input" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded border border-line text-sm">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
            className="px-3 py-1.5 rounded bg-[#ff6900] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="instance-photo-save-btn">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


function GiftOpsPanel({ rows }) {
  if (!rows.length) return <div className="text-sm text-soft">No gift records yet.</div>;
  return (
    <ul className="space-y-2" data-testid="gift-ops-list">
      {rows.map((g) => {
        const dispatch = g.dispatch_photo_url;
        const handover = g.handover_photo_url || g.dbr_handover_photo_url;
        return (
          <li key={g._id} className="border-l-2 border-[#0f766e] pl-3">
            <div className="text-sm font-semibold text-[#15110d]">{g.catalogue_label || g.benefit_id}</div>
            <div className="text-[11px] text-soft">
              <span className="font-mono">{g.month_ym}</span> ·
              <span className="ml-1 capitalize">{g.stage?.replace(/_/g, ' ')}</span>
              {g.courier_name && (
                <span className="ml-1">· {g.courier_name} {g.awb_number || ''}</span>
              )}
            </div>
            {(dispatch || handover) && (
              <div className="flex gap-2 mt-1.5" data-testid={`gift-ops-photos-${g._id}`}>
                {dispatch && (
                  <a href={dispatch} target="_blank" rel="noreferrer"
                    title="Dispatch photo" data-testid={`gift-ops-dispatch-photo-${g._id}`}>
                    <img src={dispatch} alt="dispatch"
                      className="w-14 h-14 rounded object-cover border border-line" />
                    <div className="text-[9px] uppercase text-soft text-center mt-0.5">dispatch</div>
                  </a>
                )}
                {handover && (
                  <a href={handover} target="_blank" rel="noreferrer"
                    title="Handover photo" data-testid={`gift-ops-handover-photo-${g._id}`}>
                    <img src={handover} alt="handover"
                      className="w-14 h-14 rounded object-cover border border-line" />
                    <div className="text-[9px] uppercase text-soft text-center mt-0.5">handover</div>
                  </a>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}


function FeedbackPanel({ items, dealerId, onRefresh }) {
  const [showCapture, setShowCapture] = useState(false);
  return (
    <div data-testid="feedback-panel">
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setShowCapture((s) => !s)}
          className="text-[11px] px-3 py-1 rounded bg-[#ff6900] text-white"
          data-testid="toggle-capture-btn"
        >
          {showCapture ? 'Hide capture' : '+ Capture feedback'}
        </button>
      </div>
      {showCapture && (
        <FeedbackCapture dealerId={dealerId}
          onCaptured={() => { setShowCapture(false); onRefresh(); }} />
      )}
      {items.length === 0 ? (
        <div className="text-sm text-soft mt-3">
          No feedback captured yet · LSM and WhatsApp inbound both feed this.
        </div>
      ) : (
        <ul className="space-y-3 mt-3" data-testid="feedback-list">
          {items.map((f) => (
            <FeedbackBubble key={f.feedback_id} entry={f} />
          ))}
        </ul>
      )}
    </div>
  );
}


function FeedbackBubble({ entry }) {
  const tone = SENTIMENT_TONE[entry.sentiment] || SENTIMENT_TONE.neutral;
  return (
    <li
      className="rounded-lg border p-3"
      style={{ background: tone.bg, borderColor: tone.border }}
      data-testid={`feedback-bubble-${entry.feedback_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider" style={{ color: tone.tone }}>
            {tone.icon} {entry.sentiment} ·{' '}
            {entry.benefit_id?.replace('xiaomi_', '').replace(/_/g, ' ')}
          </div>
          {entry.text && (
            <div className="text-sm mt-1.5 text-[#15110d]">"{entry.text}"</div>
          )}
        </div>
        <div className="text-right">
          {entry.rating && (
            <div className="text-[13px] font-bold" style={{ color: tone.tone }}>
              {entry.rating}/5
            </div>
          )}
          <div className="text-[10px] text-soft mt-1">{entry.source}</div>
        </div>
      </div>
      {entry.media && entry.media.length > 0 && (
        <div className="flex gap-2 mt-2 flex-wrap" data-testid="feedback-media-row">
          {entry.media.map((m, i) => (
            <a key={i} href={m.url} target="_blank" rel="noreferrer">
              {m.resource_type === 'video' ? (
                <div className="w-16 h-16 rounded bg-[#15110d] text-white flex items-center justify-center text-2xl">
                  ▶
                </div>
              ) : (
                <img
                  src={m.thumbnail_url || m.url}
                  alt={m.kind}
                  className="w-16 h-16 rounded object-cover"
                />
              )}
            </a>
          ))}
        </div>
      )}
      <div className="text-[10px] text-soft mt-2">
        {new Date(entry.captured_at).toLocaleString()} · by {entry.captured_by}
      </div>
    </li>
  );
}


function FeedbackCapture({ dealerId, onCaptured }) {
  const [text, setText] = useState('');
  const [rating, setRating] = useState(null);
  const [benefit, setBenefit] = useState('xiaomi_b01_onboarding_gift');
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text && media.length === 0) {
      toast.error('Add text or attach media');
      return;
    }
    setBusy(true);
    try {
      const monthYm = new Date().toISOString().slice(0, 7);
      await axios.post(
        `${API}/xiaomi/dealers/${dealerId}/feedback`,
        {
          benefit_id: benefit,
          month_ym: monthYm,
          text,
          rating,
          media,
        },
        auth(),
      );
      toast.success('Feedback captured');
      onCaptured?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-line rounded-lg p-4 bg-white" data-testid="feedback-capture-form">
      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <div className="text-[11px] text-soft mb-1">Benefit</div>
          <select value={benefit} onChange={(e) => setBenefit(e.target.value)}
            className="w-full px-3 py-2 rounded border border-line text-sm bg-white"
            data-testid="feedback-benefit-select">
            <option value="xiaomi_b01_onboarding_gift">Onboarding Gift</option>
            <option value="xiaomi_b02_welcome_certificate_rising">Welcome Certificate (Rising)</option>
            <option value="xiaomi_b03_luxury_kit_for_spouse">Luxury Spouse Kit</option>
            <option value="xiaomi_b04_professional_family_photoshoot">Family Photoshoot</option>
            <option value="xiaomi_b06_birthday_gift">Birthday Gift</option>
            <option value="xiaomi_b07_birthday_message_rising">Birthday Message (Rising)</option>
            <option value="xiaomi_b08_anniversary_staycation">Anniversary Staycation</option>
            <option value="xiaomi_b09_domestic_holiday">Domestic Holiday</option>
            <option value="xiaomi_b10_dinner_2">Dinner 2</option>
            <option value="xiaomi_b11_dayout">Day-out</option>
            <option value="xiaomi_b12_parent_s_gift">Parent's Gift</option>
            <option value="xiaomi_b13_gift_for_kids">Gift for Kids</option>
            <option value="xiaomi_b14_dinner_3">Dinner 3</option>
            <option value="xiaomi_b15_diwali_gift">Diwali Gift</option>
            <option value="xiaomi_b16_year_end_gift">Year End Gift</option>
          </select>
        </label>

        <label className="block">
          <div className="text-[11px] text-soft mb-1">Rating</div>
          <div className="flex gap-2" data-testid="feedback-rating-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setRating(rating === n ? null : n)}
                className={`w-9 h-9 rounded-full text-sm font-semibold ${
                  rating === n
                    ? 'bg-[#ff6900] text-white'
                    : 'bg-[#f3f4f6] text-[#15110d] hover:bg-[#fff7ed]'
                }`}
                data-testid={`feedback-rating-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </label>

        <label className="block">
          <div className="text-[11px] text-soft mb-1">What they said</div>
          <textarea
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder="What did the partner say? Any specific quotes are gold for year-end retrospective."
            rows={3}
            className="w-full px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
            data-testid="feedback-text-input"
          />
        </label>

        <MediaUploadDropzone
          dealerId={dealerId}
          onUploaded={(m) => setMedia((prev) => [...prev, m])}
          onError={(err) => toast.error(err)}
        />

        {media.length > 0 && (
          <div className="flex gap-2 flex-wrap" data-testid="capture-media-preview">
            {media.map((m, i) => (
              <div key={i} className="relative">
                {m.resource_type === 'video' ? (
                  <div className="w-12 h-12 rounded bg-[#15110d] text-white flex items-center justify-center">
                    ▶
                  </div>
                ) : (
                  <img src={m.thumbnail_url || m.url} alt={m.kind}
                    className="w-12 h-12 rounded object-cover" />
                )}
                <button onClick={() => setMedia(media.filter((_, ix) => ix !== i))}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#15110d] text-white text-[11px]">
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <button onClick={submit} disabled={busy}
          className="px-4 py-2 rounded-lg bg-[#ff6900] text-white font-semibold disabled:opacity-50"
          data-testid="feedback-save-btn">
          {busy ? 'Saving…' : 'Save feedback'}
        </button>
      </div>
    </div>
  );
}


function YearPlanPanel({ rows, spousePresent }) {
  return (
    <ul className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="year-plan-list">
      {rows.map((r) => {
        const isSpouseKit = r.benefit_id === 'xiaomi_b03_luxury_kit_for_spouse';
        const softWarn = isSpouseKit && r.eligible && !spousePresent;
        return (
          <li key={r.benefit_id} className={`flex items-center gap-2 text-[12px] ${
            r.eligible ? 'text-[#15110d]' : 'text-soft opacity-50'
          }`}>
            <span className="text-base">{r.eligible ? '✓' : '○'}</span>
            <span className="font-medium">{r.name}</span>
            {r.month && <span className="text-soft">· {r.month}</span>}
            {r.variant_for_this_tier && (
              <span className="text-[10px] text-[#7c2d12] ml-1">
                ({r.variant_for_this_tier.slice(0, 30)}{r.variant_for_this_tier.length > 30 ? '…' : ''})
              </span>
            )}
            {softWarn && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#fef3c7] border border-[#fcd34d] text-[#92400e] ml-1"
                title="Kit will be addressed to 'Mrs. <Partner>' until spouse fields are filled."
                data-testid={`spouse-soft-warn-${r.benefit_id}`}>
                spouse data missing
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}


function AuditPanel({ rows }) {
  if (!rows.length) return <div className="text-sm text-soft">No changes recorded yet.</div>;
  return (
    <ul className="space-y-2 text-[12px]" data-testid="audit-list">
      {rows.map((r, i) => (
        <li key={i} className="border-l-2 border-[#9ca3af] pl-3">
          <div className="font-medium text-[#15110d]">
            {r.field}: {String(r.before).slice(0, 40)} → {String(r.after).slice(0, 40)}
          </div>
          <div className="text-[10px] text-soft">
            {new Date(r.ts).toLocaleString()} · {r.actor_email}
            {r.note && ` · ${r.note}`}
          </div>
        </li>
      ))}
    </ul>
  );
}


/**
 * Inline-editable text field.  Click the value to edit · Esc cancels ·
 * Enter or blur saves via PATCH /api/xiaomi/dealers/{id} with header
 * `x-edit-source: inline` so the audit row is tagged correctly.
 *
 * Supports dotted-path fields (e.g., "preferences_2026.dinner_4star")
 * by getting / setting deep paths into the local form state.
 */
function InlineField({ label, field, dealerId, value, placeholder,
                      type = 'text', onSaved, multiline = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const save = async () => {
    if ((draft ?? '') === (value ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      const body = { [field]: draft === '' ? null : draft };
      await axios.patch(
        `${API}/xiaomi/dealers/${encodeURIComponent(dealerId)}`,
        body,
        { headers: { ...auth().headers, 'x-edit-source': 'inline' } },
      );
      toast.success(`${label} saved`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const cancel = () => { setDraft(value ?? ''); setEditing(false); };

  const display = value || (
    <span className="text-soft italic">{placeholder || '— add —'}</span>
  );

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)}
        className="text-left text-[12px] text-[#15110d] hover:bg-[#fff7ed] rounded px-1 -mx-1 truncate w-full"
        data-testid={`inline-field-${field.replace(/\./g,'-')}`}
        title="Click to edit">
        {display}
      </button>
    );
  }

  const InputEl = multiline ? 'textarea' : 'input';
  return (
    <div className="flex gap-1 items-start">
      <InputEl
        type={multiline ? undefined : type}
        rows={multiline ? 2 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !multiline) save();
          if (e.key === 'Escape') cancel();
        }}
        autoFocus
        disabled={saving}
        placeholder={placeholder}
        className="flex-1 px-2 py-1 rounded border border-[#ff6900] text-[12px] focus:outline-none"
        data-testid={`inline-input-${field.replace(/\./g,'-')}`}
      />
      <button onClick={save} disabled={saving}
        className="text-[10px] px-2 py-1 rounded bg-[#ff6900] text-white disabled:opacity-50"
        data-testid={`inline-save-${field.replace(/\./g,'-')}`}>
        {saving ? '…' : '✓'}
      </button>
      <button onClick={cancel} disabled={saving}
        className="text-[10px] px-2 py-1 rounded bg-[#f3f4f6] text-[#15110d]">×</button>
    </div>
  );
}


/**
 * Reach-out action buttons — Call / WhatsApp / Email — for a given
 * person + phone + email triplet.  Renders only the actions that
 * have data; gracefully hides the rest.
 */
function ReachOutRow({ name, phone, email, kind, testIdRoot }) {
  if (!name && !phone && !email) return null;
  const cleanPhone = (phone || '').toString().replace(/[^\d+]/g, '');
  const waPhone = cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone;
  return (
    <div className="flex items-center gap-1.5 text-[11px]"
      data-testid={`reachout-row-${testIdRoot}`}>
      {phone && (
        <a href={`tel:${cleanPhone}`}
          className="px-1.5 py-0.5 rounded bg-[#dbeafe] text-[#1e40af] hover:bg-[#bfdbfe] font-mono"
          title={`Call ${name || ''}`}
          data-testid={`reachout-call-${testIdRoot}`}>
          📞 {phone}
        </a>
      )}
      {phone && (
        <a href={`https://wa.me/${waPhone || cleanPhone}`} target="_blank" rel="noreferrer"
          className="px-1.5 py-0.5 rounded bg-[#dcfce7] text-[#15803d] hover:bg-[#bbf7d0]"
          title={`WhatsApp ${name || ''}`}
          data-testid={`reachout-wa-${testIdRoot}`}>
          💬 WA
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`}
          className="px-1.5 py-0.5 rounded bg-[#fef3c7] text-[#854d0e] hover:bg-[#fde68a] truncate max-w-[160px]"
          title={`Email ${email}`}
          data-testid={`reachout-email-${testIdRoot}`}>
          ✉ {email}
        </a>
      )}
      {kind && (
        <span className="text-[9px] uppercase text-soft tracking-wider ml-1">
          {kind}
        </span>
      )}
    </div>
  );
}


/**
 * Identity & Contact panel · all member-level fields, inline-editable,
 * with Call / WhatsApp / Email click-actions on every phone/email.
 *
 * Sections:
 *   • Personal — owner name, salutation, language, dates (DOB/anniversary),
 *                spouse name + DOB
 *   • Contact  — primary + secondary phone, email
 *   • Address  — outlet + residence
 *   • Preferences (2026) + History (2025) — sub-doc edits
 */
function IdentityContactPanel({ identity, dealerId, onSaved }) {
  return (
    <div className="space-y-3 text-[12px]" data-testid="identity-contact-panel">
      {/* Personal */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-soft mb-1">Personal</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <div>
            <div className="text-[10px] text-soft">Owner name</div>
            <InlineField label="Owner name" field="owner_name" dealerId={dealerId}
              value={identity.owner_name} onSaved={onSaved} />
          </div>
          <div>
            <div className="text-[10px] text-soft">Salutation</div>
            <InlineField label="Salutation" field="salutation" dealerId={dealerId}
              value={identity.salutation} onSaved={onSaved}
              placeholder="Mr / Mrs / Dr" />
          </div>
          <div>
            <div className="text-[10px] text-soft">Birthday</div>
            <InlineField label="Birthday" field="birthday" dealerId={dealerId}
              value={identity.birthday} onSaved={onSaved}
              placeholder="DD MMM (e.g. 19 Feb)" />
          </div>
          <div>
            <div className="text-[10px] text-soft">Anniversary</div>
            <InlineField label="Anniversary" field="anniversary" dealerId={dealerId}
              value={identity.anniversary} onSaved={onSaved}
              placeholder="DD MMM" />
          </div>
          <div>
            <div className="text-[10px] text-soft">Spouse name</div>
            <InlineField label="Spouse name" field="spouse_name" dealerId={dealerId}
              value={identity.spouse_name} onSaved={onSaved} />
          </div>
          <div>
            <div className="text-[10px] text-soft">Spouse birthday</div>
            <InlineField label="Spouse birthday" field="spouse_birthday" dealerId={dealerId}
              value={identity.spouse_birthday} onSaved={onSaved}
              placeholder="DD MMM" />
          </div>
          <div>
            <div className="text-[10px] text-soft">Comm. language</div>
            <InlineField label="Language" field="language_pref" dealerId={dealerId}
              value={identity.language_pref} onSaved={onSaved}
              placeholder="en / hi / ta / ..." />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-soft mb-1">Contact</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <div>
            <div className="text-[10px] text-soft">Primary phone</div>
            <InlineField label="Primary phone" field="phone_primary" dealerId={dealerId}
              value={identity.phone_primary} onSaved={onSaved}
              placeholder="+91…" />
          </div>
          <div>
            <div className="text-[10px] text-soft">Secondary phone</div>
            <InlineField label="Secondary phone" field="phone_secondary" dealerId={dealerId}
              value={identity.phone_secondary} onSaved={onSaved} />
          </div>
          <div className="col-span-2">
            <div className="text-[10px] text-soft">Email</div>
            <InlineField label="Email" field="email_primary" dealerId={dealerId}
              value={identity.email_primary} onSaved={onSaved}
              placeholder="partner@example.com" />
          </div>
        </div>
        <div className="mt-1.5">
          <ReachOutRow name={identity.owner_name} phone={identity.phone_primary}
            email={identity.email_primary} kind="partner"
            testIdRoot="partner" />
        </div>
      </div>

      {/* Address */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-soft mb-1">Address</div>
        <div className="space-y-1.5">
          <div>
            <div className="text-[10px] text-soft">Outlet</div>
            <InlineField label="Outlet address" field="outlet_address" dealerId={dealerId}
              value={identity.outlet_address} onSaved={onSaved} multiline />
          </div>
          <div>
            <div className="text-[10px] text-soft">Residence</div>
            <InlineField label="Residence address" field="residence_address" dealerId={dealerId}
              value={identity.residence_address} onSaved={onSaved} multiline />
          </div>
        </div>
      </div>

      {/* 2025 history + 2026 preferences */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-soft mb-1">
          2025 history · 2026 preferences
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <div>
            <div className="text-[10px] text-soft">2025 restaurant visits</div>
            <InlineField label="2025 restaurants" field="history_2025.restaurant_visits"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.history_2025?.restaurant_visits} />
          </div>
          <div>
            <div className="text-[10px] text-soft">2025 weekend getaway</div>
            <InlineField label="2025 weekend" field="history_2025.weekend_getaway"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.history_2025?.weekend_getaway} />
          </div>
          <div>
            <div className="text-[10px] text-soft">2025 holiday</div>
            <InlineField label="2025 holiday" field="history_2025.holiday_destination"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.history_2025?.holiday_destination} />
          </div>
          <div>
            <div className="text-[10px] text-soft">2026 dinner pref</div>
            <InlineField label="2026 dinner" field="preferences_2026.dinner_4star"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.preferences_2026?.dinner_4star} />
          </div>
          <div>
            <div className="text-[10px] text-soft">2026 weekend / anniv dinner</div>
            <InlineField label="2026 weekend" field="preferences_2026.weekend_anniversary_dinner"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.preferences_2026?.weekend_anniversary_dinner} />
          </div>
          <div>
            <div className="text-[10px] text-soft">2026 holiday</div>
            <InlineField label="2026 holiday" field="preferences_2026.holiday_ooty_first"
              dealerId={dealerId} onSaved={onSaved}
              value={identity.preferences_2026?.holiday_ooty_first} />
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Xiaomi Chain panel · DBR / ZSM / ZH linkage with click-to-call /
 * WhatsApp on every contact.  This is the panel Ayush opens before
 * making any partner outreach so he knows who's the escalation chain.
 */
function XiaomiChainPanel({ identity, dealerId, onSaved }) {
  return (
    <div className="space-y-3 text-[12px]" data-testid="xiaomi-chain-panel">
      {/* DBR */}
      <div className="border-l-2 border-[#0f766e] pl-3">
        <div className="text-[10px] uppercase tracking-wider text-soft">DBR · Distributor</div>
        <div className="font-semibold text-[#15110d]">
          <InlineField label="DBR name" field="dbr_name" dealerId={dealerId}
            value={identity.dbr_name} onSaved={onSaved}
            placeholder="DBR / Distributor name" />
        </div>
      </div>

      {/* ZSM */}
      <div className="border-l-2 border-[#ff6900] pl-3">
        <div className="text-[10px] uppercase tracking-wider text-soft">ZSM · Zonal Sales Manager</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div>
            <div className="text-[10px] text-soft">Name</div>
            <InlineField label="ZSM name" field="zsm_name" dealerId={dealerId}
              value={identity.zsm_name} onSaved={onSaved} />
          </div>
          <div>
            <div className="text-[10px] text-soft">Mobile</div>
            <InlineField label="ZSM mobile" field="zsm_mobile" dealerId={dealerId}
              value={identity.zsm_mobile} onSaved={onSaved}
              placeholder="+91…" />
          </div>
        </div>
        <div className="mt-1">
          <ReachOutRow name={identity.zsm_name} phone={identity.zsm_mobile}
            kind="ZSM" testIdRoot="zsm" />
        </div>
      </div>

      {/* ZH */}
      <div className="border-l-2 border-[#7c3aed] pl-3">
        <div className="text-[10px] uppercase tracking-wider text-soft">ZH · Zonal Head</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <div>
            <div className="text-[10px] text-soft">Name</div>
            <InlineField label="ZH name" field="zh_name" dealerId={dealerId}
              value={identity.zh_name} onSaved={onSaved} />
          </div>
          <div>
            <div className="text-[10px] text-soft">Mobile</div>
            <InlineField label="ZH mobile" field="zh_mobile" dealerId={dealerId}
              value={identity.zh_mobile} onSaved={onSaved}
              placeholder="+91…" />
          </div>
        </div>
        <div className="mt-1">
          <ReachOutRow name={identity.zh_name} phone={identity.zh_mobile}
            kind="ZH" testIdRoot="zh" />
        </div>
      </div>

      <div className="text-[10px] text-soft pt-2 border-t border-line">
        Escalation chain · partner <span className="text-[#15110d]">→</span> DBR
        <span className="text-[#15110d]"> →</span> ZSM
        <span className="text-[#15110d]"> →</span> ZH.
        Day-10 chases call ZSM, Day-15 chases escalate to ZH.
      </div>
    </div>
  );
}


export default function XiaomiPartner360() {
  const { retailerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/xiaomi/dealers/${retailerId}/360`, auth());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load partner');
    } finally { setLoading(false); }
  }, [retailerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="xiaomi-theme p-8" data-testid="partner-360-loading">Loading partner 360…</div>;
  }
  if (!data) {
    return <div className="xiaomi-theme p-8" data-testid="partner-360-empty">Partner not found.</div>;
  }

  const id = data.identity;
  const p = data.pinned;

  const downloadProfilePdf = async (retailerId, outletName) => {
    try {
      const r = await axios.get(
        `${API}/xiaomi/dealers/${encodeURIComponent(retailerId)}/profile.pdf`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (outletName || retailerId).replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `xiaomi_partner_${retailerId}_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Profile PDF downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate PDF');
    }
  };

  return (
    <div className="xiaomi-theme p-5 space-y-3" data-testid="partner-360-page">
      {/* Header · compact */}
      <div className="x-card p-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#ff6900,#cc5500)' }}>
            {(id.owner_name || id.outlet_name || '?').split(' ').filter(Boolean).slice(0, 2).map(x => x[0]).join('').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <TierChip tier={id.tier} />
              {id.zone && <span className="text-[10px] px-2 py-0.5 rounded bg-[#faf6ef] border border-line">
                {id.zone}
              </span>}
              {id.language_pref && <span className="text-[10px] px-2 py-0.5 rounded bg-[#faf6ef] border border-line uppercase">
                {id.language_pref}
              </span>}
              <span className="text-[10px] font-mono text-soft">#{data.retailer_id}</span>
            </div>
            <h1 className="text-2xl leading-tight" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
              {id.outlet_name || id.owner_name}
            </h1>
            <div className="text-[12px] text-[#374151] mt-1 flex gap-3 flex-wrap">
              <span data-testid="hdr-owner">👤 {id.owner_name || '—'}</span>
              <span data-testid="hdr-spouse">💍 {id.spouse_name || id.spouse_first_name || 'No spouse'}</span>
              <span data-testid="hdr-city">📍 {id.city || '—'}, {id.state || '—'}</span>
              <span data-testid="hdr-dbr">🏢 DBR: {id.dbr_name || '—'}</span>
              {id.zsm_name && <span data-testid="hdr-zsm">👔 ZSM: {id.zsm_name}</span>}
              {id.zh_name && <span data-testid="hdr-zh">🎯 ZH: {id.zh_name}</span>}
            </div>
            {/* Quick reach-out row · partner + ZSM + ZH on a single bar */}
            <div className="mt-2 flex flex-wrap gap-1.5 items-center"
              data-testid="quick-reachout-bar">
              {id.phone_primary && (
                <a href={`tel:${id.phone_primary}`}
                  className="px-2 py-0.5 rounded-md bg-[#dbeafe] text-[#1e40af] hover:bg-[#bfdbfe] text-[11px] font-mono"
                  data-testid="quick-call-partner">
                  📞 {id.phone_primary}
                </a>
              )}
              {id.phone_primary && (
                <a href={`https://wa.me/${(id.phone_primary || '').replace(/[^\d]/g,'')}`}
                  target="_blank" rel="noreferrer"
                  className="px-2 py-0.5 rounded-md bg-[#dcfce7] text-[#15803d] hover:bg-[#bbf7d0] text-[11px]"
                  data-testid="quick-wa-partner">
                  💬 WhatsApp partner
                </a>
              )}
              {id.email_primary && (
                <a href={`mailto:${id.email_primary}`}
                  className="px-2 py-0.5 rounded-md bg-[#fef3c7] text-[#854d0e] hover:bg-[#fde68a] text-[11px]"
                  data-testid="quick-email-partner">
                  ✉ Email
                </a>
              )}
              {id.zsm_mobile && (
                <a href={`tel:${id.zsm_mobile}`}
                  className="px-2 py-0.5 rounded-md bg-[#fff7ed] text-[#7c2d12] hover:bg-[#ffedd5] text-[11px] font-mono"
                  data-testid="quick-call-zsm">
                  📞 ZSM {id.zsm_mobile}
                </a>
              )}
              {id.zsm_mobile && (
                <a href={`https://wa.me/${(id.zsm_mobile || '').replace(/[^\d]/g,'')}`}
                  target="_blank" rel="noreferrer"
                  className="px-2 py-0.5 rounded-md bg-[#fff7ed] text-[#7c2d12] hover:bg-[#ffedd5] text-[11px]"
                  data-testid="quick-wa-zsm">
                  💬 WA ZSM
                </a>
              )}
              {id.zh_mobile && (
                <a href={`tel:${id.zh_mobile}`}
                  className="px-2 py-0.5 rounded-md bg-[#ede9fe] text-[#5b21b6] hover:bg-[#ddd6fe] text-[11px] font-mono"
                  data-testid="quick-call-zh">
                  📞 ZH {id.zh_mobile}
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
            <button onClick={() => navigate('/xiaomi/partners')}
              className="text-soft hover:text-ink text-2xl" data-testid="partner-360-close">×</button>
            <button onClick={() => downloadProfilePdf(data.retailer_id, id.outlet_name)}
              className="px-2.5 py-1 rounded-md bg-[#15110d] text-white text-[11px] hover:bg-[#2a2419] font-medium whitespace-nowrap"
              title="Download A4 partner dossier PDF · for offline meetings & LSM hand-off"
              data-testid="partner-360-download-pdf">
              ⬇ Download profile PDF
            </button>
          </div>
        </div>

        {/* Pinned standing instructions · compact */}
        <div className="mt-3 rounded-lg px-3 py-2 text-[12px]"
          style={{
            background: p.has_directive ? '#fff7ed' : '#fef3c7',
            border: `1px solid ${p.has_directive ? '#fb923c' : '#fcd34d'}`,
          }}
          data-testid="pinned-directive">
          <span className="font-semibold mr-1" style={{ color: p.has_directive ? '#7c2d12' : '#92400e' }}>
            📌
          </span>
          {p.has_directive ? (
            <span className="text-[#15110d] whitespace-pre-wrap">{p.concierge_directive}</span>
          ) : (
            <span style={{ color: '#92400e' }}>
              No directive on file · LSM calling blind. Populate from v4 master before next interaction.
            </span>
          )}
        </div>
      </div>

      {/* Counts strip · sticky · denser */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center sticky top-0 z-10 bg-[#fbfaf7] py-2 -mx-1 px-1"
        data-testid="counts-bar">
        {Object.entries(data.panel_counts).map(([k, v]) => (
          <div key={k} className="bg-white border border-line rounded-md py-1.5" data-testid={`count-${k}`}>
            <div className="text-[9px] uppercase tracking-wider text-soft">{k.replace(/_/g, ' ')}</div>
            <div className="text-xl font-bold text-[#15110d] leading-tight">{v}</div>
          </div>
        ))}
      </div>

      {/* Two-column main grid · tighter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="Identity & contact" testId="panel-identity">
          <IdentityContactPanel identity={id} dealerId={data.retailer_id}
            onSaved={load} />
        </Panel>

        <Panel title="Xiaomi chain · escalation" testId="panel-chain">
          <XiaomiChainPanel identity={id} dealerId={data.retailer_id}
            onSaved={load} />
        </Panel>

        <Panel title="Active workflows" count={data.active_instances.length}
          testId="panel-active">
          <ActiveInstancesPanel instances={data.active_instances}
            dealerId={data.retailer_id}
            dealerLanguage={id.language_pref}
            onRefresh={load} />
        </Panel>

        <Panel title="Feedback ledger" count={data.feedback.length}
          testId="panel-feedback">
          <FeedbackPanel items={data.feedback} dealerId={data.retailer_id} onRefresh={load} />
        </Panel>

        <Panel title="Gift operations" count={data.gift_ops.length}
          testId="panel-gift-ops">
          <GiftOpsPanel rows={data.gift_ops} />
        </Panel>

        <Panel title="2026 Plan (eligibility)" count={data.eligibility.filter(e => e.eligible).length}
          testId="panel-year-plan">
          <YearPlanPanel rows={data.eligibility}
            spousePresent={data.pinned?.spouse_present} />
        </Panel>

        <Panel title="Audit trail" count={data.audit.length}
          testId="panel-audit">
          <AuditPanel rows={data.audit} />
        </Panel>

        <Panel title="Zoho tickets" count={data.zoho_tickets.length}
          testId="panel-zoho">
          {data.zoho_tickets.length === 0 ? (
            <div className="text-[12px] text-soft">
              No Zoho tickets yet · first ticket fires when Nisha sets
              ZOHO_XIAOMI_DEPARTMENT_ID and a workflow step opens.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="zoho-tickets-list">
              {data.zoho_tickets.map((t) => (
                <li key={t.ticket_id} className="text-sm">
                  <a href={t.ticket_url || '#'} target="_blank" rel="noreferrer"
                    className="text-[#1e40af] hover:underline">
                    #{t.ticket_id} ↗
                  </a>
                  <span className="text-[11px] text-soft ml-2">
                    {t.benefit_id} · {t.stage}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
